import { useNavigate } from 'react-router-dom';
import { Home, ArrowRight } from 'lucide-react';
import { useResearch } from '../hooks/useResearch';

const Research = () => {
  const navigate = useNavigate();
  const { research, loading, error } = useResearch();

  const getStatusColor = (status) => {
    return 'bg-black-500/20 text-gray-400';
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Fixed Navigation - only on mobile */}
      <nav className="sm:hidden fixed top-0 left-0 right-0 bg-black border-b border-white/10 z-50">
        <div className="px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <Home className="w-4 h-4" />
              <span className="text-sm">Home</span>
            </button>

            <button
              onClick={() => navigate('/projects')}
              className="text-white/60 hover:text-white transition-colors text-sm"
            >
              Projects
            </button>

            <button
              onClick={() => navigate('/writings')}
              className="text-white/60 hover:text-white transition-colors text-sm"
            >
              Writings
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content - padding-top only on mobile for fixed nav */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-8 pb-8 sm:pb-12">
        {/* Navigation - desktop only */}
        <nav className="hidden sm:block mb-12">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>

            <button
              onClick={() => navigate('/projects')}
              className="text-white/60 hover:text-white transition-colors"
            >
              Projects
            </button>

            <button
              onClick={() => navigate('/writings')}
              className="text-white/60 hover:text-white transition-colors"
            >
              Writings
            </button>
          </div>
        </nav>
        {/* Header */}
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-light">Research Works</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-white/40">Loading research...</div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-red-400">Error loading research: {error}</div>
        )}

        {/* Research List */}
        {!loading && !error && (
          <div className="space-y-6 sm:space-y-8">
            {research.length === 0 ? (
              <div className="text-white/40">No research publications yet. Check back soon!</div>
            ) : (
              research.map((item) => (
                <div
                  key={item.id}
                  className="group border-b border-white/10 pb-6 sm:pb-8 last:border-b-0"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-3">
                    <h3 className="text-xl sm:text-2xl font-light text-white group-hover:text-white/80 transition-colors flex-1">
                      {item.title}
                    </h3>
                    <span className={`text-xs px-2 py-1 rounded whitespace-nowrap self-start ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-white/40 text-xs sm:text-sm mb-3">
                    <span>{item.venue}</span>
                    <span>•</span>
                    <span>{item.type}</span>
                    <span>•</span>
                    <span>{item.date}</span>
                  </div>

                  <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-3">
                    {item.description}
                  </p>

                  <div className="text-white/40 text-xs sm:text-sm mb-3 break-words">
                    {item.authors}
                  </div>

                  {item.link && item.link !== '#' && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs sm:text-sm transition-colors"
                    >
                      <span>View Publication</span>
                      <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Research;